/**
 * Map-focus signal — the one-shot "force the camera to this entity NOW"
 * store that backs the list panels' Phase-2 **"Select on Map"** affordance
 * (the 2026-06-09 two-slot selection-sync redesign).
 *
 * # Why this exists — the force-recenter vs. pan-if-not-in-view split
 *
 * The map layers' camera effects (``snapshot-layer.tsx``,
 * ``detection-layer.tsx``, ``alert-layer.tsx``) pan to the ``mapSelected``
 * entity *only when it isn't already in the viewport* (ADR-024 §5 /
 * REQ-UI-051 — "pans … to centre the coordinates" when off-screen). A
 * **map-glyph click** must keep that gentle behaviour: the operator
 * clicked something they can already see, so yanking the camera would be
 * disorienting.
 *
 * The list "Select on Map" affordance is different (architect ruling,
 * 2026-06-09 — "Ring + recenter + focus"): the entity's glyph may be
 * off-screen, and the gesture's whole point is to make it the camera's
 * full focus — so it must **always** recenter, even if the entity happens
 * to already be in view. ``mapSelected`` alone can't distinguish the two
 * originators (a glyph click and a "Select on Map" both set it to the same
 * value), so we need a *separate* signal that only the list path raises.
 *
 * # The mechanism — a monotonic nonce
 *
 * ``requestMapFocus(value)`` records the focus ``target`` and bumps a
 * monotonically-increasing ``nonce``. The map layers add an effect keyed
 * on ``nonce`` that, when it fires, force-recenters (``easeTo``, no
 * in-viewport check) iff the focus ``target`` matches the layer's kind +
 * the current ``mapSelected`` id. Because a glyph click never calls
 * ``requestMapFocus``, the nonce never bumps on the map-originated path —
 * so a glyph click cannot force-recenter. That is the divergence the
 * redesign guarantees (and the regression guard in the layer tests).
 *
 * A **nonce** (rather than just the target value) is load-bearing: two
 * consecutive "Select on Map" gestures on the *same* row must each
 * recenter, even though ``target`` is unchanged between them. Keying the
 * effect on a value that changes every request guarantees the effect
 * re-runs on every gesture. The nonce is never read for its magnitude —
 * only its *change* matters — so wraparound is a non-issue.
 *
 * # One-shot, not durable state
 *
 * Unlike the selection store, this store holds no operator-meaningful
 * state — it is a transient command channel. Nothing renders from it; the
 * only consumers are the three map-layer camera effects. ``target`` is
 * retained only so the firing effect can resolve which entity to recenter
 * on; it is never the source of truth for "what is selected" (that's the
 * selection store's ``mapSelected``).
 *
 * # Coordinate resolution stays at the layer
 *
 * This store carries only the ``{kind, id}`` target — never coordinates.
 * Each layer already owns the authoritative position for its kind (the
 * snapshot layer's ``snapshot`` prop for platforms; the detections /
 * alerts stores' ``byId`` for detection / alert glyphs), including the
 * null-position gate (REQ-INGEST-044 / REQ-UI-069). Centralising
 * coordinate lookup here would duplicate that resolution and risk drift,
 * so the layer resolves coords at fire time exactly as its pan-if-not-in-
 * view effect already does.
 *
 * # Testing
 *
 * Module-level state requires a reset between cases::
 *
 *     beforeEach(() => {
 *       useMapFocusStore.setState({ nonce: 0, target: null });
 *     });
 *
 * Production code never calls ``setState`` directly — always via
 * ``requestMapFocus``.
 *
 * Cross-references:
 *   - REQ-UI-027 (Zustand for client state).
 *   - REQ-UI-051 (synchronized-selection bus contract).
 *   - REQ-UI-096 (cross-pane navigation / "Select on Map").
 *   - ADR-024 §5 (selection bus — 200 ms ease, pan-if-not-in-view).
 *   - selection/selection-store.ts (the two-slot bus this complements).
 *   - docs/engineering/detail/storyboards/flows/Q-side-by-side-map-platforms/README.md.
 */
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { SelectionValue } from "@/selection/selection-store";

/**
 * The map-focus store shape — a one-shot command channel, not durable
 * state.
 *
 * Cross-references:
 *   - REQ-UI-051 / REQ-UI-096; ADR-024 §5.
 */
export interface MapFocusState {
  /**
   * Monotonically-increasing request counter. Bumped on every
   * :func:`requestMapFocus` call. The map-layer effects key on this so a
   * repeat "Select on Map" on the same entity still recenters. Read only
   * for its *change*, never its magnitude.
   */
  readonly nonce: number;
  /**
   * The entity the most recent focus request targeted, or ``null`` before
   * the first request. Resolved by the firing layer effect to look up the
   * entity's coordinates; never a selection source of truth.
   */
  readonly target: SelectionValue | null;
  /**
   * Raise a force-recenter request for ``value`` — records it as the
   * ``target`` and bumps ``nonce``. The list "Select on Map" path calls
   * this *in addition to* :func:`selectOnMap` (which sets both selection
   * slots); the map-glyph path never calls it (so glyph clicks can't
   * force-recenter — the redesign's intended divergence).
   */
  readonly requestMapFocus: (value: SelectionValue) => void;
}

/**
 * The module-level map-focus Zustand store.
 *
 * Consume the focus signal via :func:`useMapFocusSignal` (the layer
 * effects) and raise requests via :func:`useRequestMapFocus` (the panels).
 * For non-React write sites, use
 * ``useMapFocusStore.getState().requestMapFocus(...)``.
 */
export const useMapFocusStore = create<MapFocusState>((set) => ({
  nonce: 0,
  target: null,
  requestMapFocus: (value) => {
    set((state) => ({ nonce: state.nonce + 1, target: value }));
  },
}));

/**
 * The map-focus signal ``{ nonce, target }``, for the map-layer camera
 * effects.
 *
 * A layer keys its force-recenter effect on ``nonce`` and reads ``target``
 * inside it to decide whether the request is for its kind + the current
 * ``mapSelected`` id. ``useShallow`` keeps the returned object referentially
 * stable between unrelated store writes.
 *
 * Cross-references:
 *   - ADR-024 §5; REQ-UI-051.
 */
export function useMapFocusSignal(): {
  nonce: number;
  target: SelectionValue | null;
} {
  return useMapFocusStore(useShallow((state) => ({ nonce: state.nonce, target: state.target })));
}

/**
 * The stable :func:`requestMapFocus` action, for the list panels' "Select
 * on Map" menu items.
 *
 * The action reference is stable across renders (Zustand contract), so
 * it's safe in effect/callback dependency arrays.
 *
 * Cross-references:
 *   - REQ-UI-096; ADR-024 §5.
 */
export function useRequestMapFocus(): MapFocusState["requestMapFocus"] {
  return useMapFocusStore((state) => state.requestMapFocus);
}
